import { Link } from "react-router-dom";
import {
  ArrowRight,
  ChatCircleDots,
  ClockCounterClockwise,
  Flag,
  Handshake,
  TrendUp,
  Users,
} from "@phosphor-icons/react";
import ProductPageShell from "@/components/ProductPageShell";
import { trackPublicEvent } from "@/lib/publicAnalytics";

const source = "product_relationship_memory";
const activePath = "/product/relationship-memory";

const memoryLayers = [
  {
    icon: Users,
    label: "Stakeholders",
    title: "Remember who matters and how they entered the story.",
    copy: "Keep the champion, approver, blocker, handoff, and ownership changes attached to the relationship instead of scattered across notes.",
  },
  {
    icon: Handshake,
    label: "Commitments",
    title: "Carry promises and open loops forward.",
    copy: "Know what you promised, what the customer promised, what is still unresolved, and which follow-up should shape the next conversation.",
  },
  {
    icon: Flag,
    label: "Risks and objections",
    title: "See what is still alive instead of rereading everything.",
    copy: "A topic being mentioned once is not the same as an objection being resolved. Callsect keeps the active relationship state visible.",
  },
  {
    icon: TrendUp,
    label: "What changed",
    title: "Understand the movement across calls.",
    copy: "Track when urgency rises, confidence weakens, ownership changes, or the relationship moves toward expansion, renewal, or risk.",
  },
];

const timeline = [
  {
    stage: "Discovery",
    state: "Champion engaged",
    detail: "The problem is real, but Finance has not yet joined the decision.",
  },
  {
    stage: "Follow-up",
    state: "Priority challenged",
    detail: "The champion still wants to move, but the economic approver asks why this needs budget now.",
  },
  {
    stage: "Current state",
    state: "Decision path created",
    detail: "Finance validation is booked and Security now follows a dated go / no-go decision.",
  },
];

function track(section, ctaId) {
  trackPublicEvent("cta_click", { source, section, cta_id: ctaId });
}

export default function ProductRelationshipMemoryPage() {
  return (
    <ProductPageShell source={source} activePath={activePath}>
      <section className="product-feature-hero px-5 pb-16 pt-16 sm:px-8 sm:pb-24 sm:pt-28">
        <div className="product-feature-hero__glow" aria-hidden="true" />
        <div className="relative mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.94fr_1.06fr] lg:items-center lg:gap-16">
          <div>
            <div className="landing-anti-summary-pill mb-6 inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.2em] marketing-text-secondary">
              <span className="landing-anti-summary-icon" aria-hidden="true">
                <ArrowRight size={14} weight="bold" />
              </span>
              <span>Relationship memory</span>
            </div>
            <h1 className="product-feature-hero__title landing-hero-title max-w-3xl text-[2.7rem] font-semibold leading-[1.01] tracking-[-0.055em] sm:text-6xl lg:text-7xl">
              <span className="product-feature-hero__title-base block">Never lose the</span>
              <span className="product-feature-hero__title-accent landing-hero-spectrum landing-hero-promise block">thread with a customer.</span>
            </h1>
            <p className="product-feature-hero-subcopy landing-hero-subcopy mt-7 max-w-2xl text-lg font-medium leading-8 marketing-text-secondary sm:text-xl sm:leading-9">
              A single call can be summarized. A relationship has to be remembered. Callsect carries forward the people, promises, risks, objections, and changes that should still matter when the next conversation starts.
            </p>
            <div className="product-feature-hero__actions mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/login?mode=register" onClick={() => track("hero", "sign_up_free")} className="product-feature-hero-primary landing-signup-primary-cta inline-flex items-center justify-center rounded-xl marketing-primary-action border px-5 py-3.5 font-medium transition">
                Sign up free
              </Link>
              <Link to="/demo" onClick={() => track("hero", "relationship_demo")} className="product-feature-hero-secondary landing-hero-secondary-cta inline-flex items-center justify-center gap-2 rounded-xl marketing-secondary-action border px-5 py-3.5 font-medium marketing-text-secondary-strong transition">
                See Demo
                <ArrowRight size={17} weight="bold" />
              </Link>
            </div>
          </div>

          <div className="product-relationship-thesis" aria-label="How Callsect relationship memory differs">
            <div>
              <span>A CRM remembers fields.</span>
              <span>A summarizer remembers words.</span>
              <strong>Callsect remembers the relationship.</strong>
            </div>
            <ClockCounterClockwise size={42} weight="duotone" aria-hidden="true" />
          </div>
        </div>
      </section>

      <section className="product-feature-section marketing-band border-y px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto max-w-7xl">
          <div className="product-feature-section__intro">
            <span>What stays with the customer</span>
            <h2>Not a pile of old notes. A usable relationship state.</h2>
            <p>Callsect keeps the few pieces of context that materially change how you should enter the next call.</p>
          </div>
          <div className="product-feature-grid product-feature-grid--four mt-12">
            {memoryLayers.map(({ icon: Icon, label, title, copy }) => (
              <article className="product-feature-card" key={label}>
                <Icon size={24} weight="duotone" aria-hidden="true" />
                <span>{label}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="product-feature-section px-5 py-16 sm:px-8 sm:py-24">
        <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:items-start lg:gap-16">
          <div className="product-feature-sticky product-feature-sticky--no-kicker">
            <h2>The point is not to remember more. It is to see what changed.</h2>
            <p>
              The same customer can sound positive in every meeting while the commercial reality quietly shifts underneath. Relationship memory makes that movement explicit.
            </p>
          </div>

          <div className="product-relationship-timeline">
            {timeline.map((item, index) => (
              <article key={item.stage}>
                <div className="product-relationship-timeline__index">0{index + 1}</div>
                <div>
                  <span>{item.stage}</span>
                  <h3>{item.state}</h3>
                  <p>{item.detail}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="product-feature-band px-5 py-14 sm:px-8 sm:py-16">
        <div className="marketing-highlight-band mx-auto flex max-w-6xl flex-col gap-6 rounded-3xl border p-7 sm:p-9 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <ChatCircleDots size={25} weight="duotone" aria-hidden="true" />
            <h2>Walk into the next call knowing where you left off.</h2>
            <p>The relationship page turns history into the context that should shape what happens next.</p>
          </div>
          <Link to="/product/prepare-next-call" className="product-feature-inline-link">
            See next-call preparation
            <ArrowRight size={17} weight="bold" />
          </Link>
        </div>
      </section>
    </ProductPageShell>
  );
}
